import { useEffect, useRef, useState } from 'react';

import { useSongQueue } from '~/modules/song-queue/hooks';
import { SongQueueItem } from '~/modules/song-queue/song-queue';

/** How long the room has to object before the running order carries on by itself. */
export const NEXT_UP_SECONDS = 12;

/**
 * The song the running order will play next, and how long is left before it does.
 *
 * `armed` is the caller's: the results step only counts down once the score has finished counting,
 * and neither step counts down at all with an empty queue - there is nothing to announce and nothing
 * to advance to.
 *
 * Any interaction stops the clock for good. A countdown that took the screen away from somebody
 * mid-rename, or mid-choosing what to queue next, would be worse than no countdown at all - and it
 * is what "Back cancels the countdown and stays" means once Back is not the only way to say wait.
 */
export function useNextUp(armed: boolean, onElapsed: () => void) {
  const queue = useSongQueue();
  const next: SongQueueItem | undefined = queue[0];
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const fired = useRef(false);

  // Only a running order that was already there when the button went live counts down. A song added
  // from "More like this" while the screen is open is somebody lining up the evening, not asking to
  // be moved on in twelve seconds - it still gets named on the button, just without a clock.
  const waitingWhenArmed = useRef<boolean | null>(null);
  if (armed && waitingWhenArmed.current === null) waitingWhenArmed.current = next !== undefined;

  const running = armed && waitingWhenArmed.current === true && next !== undefined && !cancelled;

  useEffect(() => {
    if (!running) {
      setSecondsLeft(null);

      return;
    }

    setSecondsLeft(NEXT_UP_SECONDS);
    const interval = setInterval(() => setSecondsLeft((left) => Math.max(0, (left ?? NEXT_UP_SECONDS) - 1)), 1_000);

    return () => clearInterval(interval);
  }, [running]);

  useEffect(() => {
    if (!running) return;

    // Capture, because `useKeyboardNav` handles arrow keys on the way down and a bubble-phase
    // listener on `window` would never hear the ones that moved the focus.
    const stop = () => setCancelled(true);
    window.addEventListener('pointerdown', stop, true);
    window.addEventListener('keydown', stop, true);

    return () => {
      window.removeEventListener('pointerdown', stop, true);
      window.removeEventListener('keydown', stop, true);
    };
  }, [running]);

  useEffect(() => {
    if (secondsLeft !== 0 || fired.current) return;

    fired.current = true;
    onElapsed();
  }, [secondsLeft, onElapsed]);

  return { next, secondsLeft: running ? secondsLeft : null };
}
